import { useState } from 'react';
import Button from '@/components/ui/Button';
import Input from '@/components/ui/Input';
import BackgroundImagesTab from './BackgroundImagesTab';
import AlertsTab from './AlertsTab';
import { usePermissions } from '@/hooks';

interface ControlSettings {
  itemsPerPage: number;
  monthlyCreditsPrice: number;
  annualCreditsPrice: number;
  allowedPeriods: {
    months: boolean;
    years: boolean;
  };
  maxTransactionsPerAgent: number;
}

const General = () => {
  const [activeTab, setActiveTab] = useState<'background' | 'control' | 'alerts' | 'content'>('background');
  const { hasAnyPermission, isAdmin } = usePermissions();

  const [controlSettings, setControlSettings] = useState<ControlSettings>({
    itemsPerPage: 50,
    monthlyCreditsPrice: 0,
    annualCreditsPrice: 0,
    allowedPeriods: {
      months: true,
      years: false
    },
    maxTransactionsPerAgent: 12
  });

  const tabs = [
    { key: 'background', label: 'Home background', show: hasAnyPermission(['HOME_PAGE_SLIDES_MANAGE']) },
    { key: 'control', label: 'Control', show: isAdmin() },
    { key: 'content', label: 'Contenido', show: isAdmin() },
    { key: 'alerts', label: 'Mensajes alerta', show: isAdmin() }
  ];

  return (
    <div className="w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      <h1 className="text-3xl font-bold mb-8 text-gray-900">CONFIGURACIÓN</h1>

      {/* Tab Navigation */}
      <div className="border-b border-gray-200 mb-8">
        <nav className="-mb-px flex space-x-8">
          {tabs.filter(tab => tab.show).map((tab) => (
            <button
              key={tab.key}
              onClick={() => setActiveTab(tab.key as 'background' | 'control' | 'alerts')}
              className={` cursor-pointer py-4 px-1 border-b-2 font-medium text-sm ${
                activeTab === tab.key
                  ? 'border-indigo-500 text-indigo-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              {tab.label}
            </button>
          ))}
        </nav>
      </div>

      {/* Background Images Tab */}
      {activeTab === 'background' && (
        <BackgroundImagesTab />
      )}

      {/* Control Tab */}
      {activeTab === 'control' && (
        <div className="space-y-8">
          {/* General Section */}
          <div className="bg-white p-6 rounded-lg shadow-sm">
            <h3 className="text-lg font-semibold text-gray-800 mb-4">GENERAL</h3>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              <Input
                label="Cantidad de ítems por páginas"
                type="number"
                value={controlSettings.itemsPerPage}
                onChange={(e) => setControlSettings(prev => ({
                  ...prev,
                  itemsPerPage: parseInt(e.target.value) || 0
                }))}
              />
              <Input
                label="Precio de créditos Mensual"
                type="number"
                value={controlSettings.monthlyCreditsPrice}
                onChange={(e) => setControlSettings(prev => ({
                  ...prev,
                  monthlyCreditsPrice: parseFloat(e.target.value) || 0
                }))}
              />
              <Input
                label="Precio de créditos Anual"
                type="number"
                value={controlSettings.annualCreditsPrice}
                onChange={(e) => setControlSettings(prev => ({
                  ...prev,
                  annualCreditsPrice: parseFloat(e.target.value) || 0
                }))}
              />
            </div>
          </div>

          {/* Control Agentes Section */}
          <div className="bg-white p-6 rounded-lg shadow-sm">
            <h3 className="text-lg font-semibold text-gray-800 mb-4">CONTROL AGENTES</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-3">
                  Período permitidos para Agentes
                </label>
                <div className="space-y-2 bg-gray-50 p-4 rounded-md">
                  <div className="flex items-center">
                    <input
                      type="checkbox"
                      id="months"
                      checked={controlSettings.allowedPeriods.months}
                      onChange={(e) => setControlSettings(prev => ({
                        ...prev,
                        allowedPeriods: { ...prev.allowedPeriods, months: e.target.checked }
                      }))}
                      className="rounded border-gray-300 text-indigo-600 focus:ring-indigo-500"
                    />
                    <label htmlFor="months" className="ml-2 text-sm text-gray-700">
                      MESES
                    </label>
                  </div>
                  <div className="flex items-center">
                    <input
                      type="checkbox"
                      id="years"
                      checked={controlSettings.allowedPeriods.years}
                      onChange={(e) => setControlSettings(prev => ({
                        ...prev,
                        allowedPeriods: { ...prev.allowedPeriods, years: e.target.checked }
                      }))}
                      className="rounded border-gray-300 text-indigo-600 focus:ring-indigo-500"
                    />
                    <label htmlFor="years" className="ml-2 text-sm text-gray-700">
                      AÑOS
                    </label>
                  </div>
                </div>
              </div>
              <Input
                label="Máxima Trans. para agente"
                type="number"
                value={controlSettings.maxTransactionsPerAgent}
                onChange={(e) => setControlSettings(prev => ({
                  ...prev,
                  maxTransactionsPerAgent: parseInt(e.target.value) || 0
                }))}
              />
            </div>
          </div>

          <div className="flex justify-end">
            <Button variant="primary">
              Guardar Configuración
            </Button>
          </div>
        </div>
      )}

      {/* Alert Messages Tab */}
      {activeTab === 'alerts' && (
        <AlertsTab />
      )}

      {activeTab == 'content' && (
        <div className="space-y-8">
          {/* General Section */}
          <div className="bg-white p-6 rounded-lg shadow-sm">
            <h3 className="text-lg font-semibold text-gray-800 mb-4">GENERAL</h3>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            </div>
          </div>
        </div>)
      }

      {/* Image modal moved into BackgroundImagesTab */}
    </div>
  );
}

export default General;
